import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoRounded';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { COLORS } from '../../../utils/Application_Constants';

const styles = {
    container: {
        mt: 2,
        p: 2,
        borderRadius: '8px',
        background: COLORS.LT_PEPPER10,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    iconContainer: { display: 'flex', alignItems: 'center', gap: 1 },
    icon: { color: COLORS.LT_PEPPER, width: '24px', height: '24px' },
    iconText: {
        fontWeight: 400,
        color: COLORS.LT_PEPPER150,
        lineHeight: '22px',
        fontSize: '14px',
    },
    lineContainer: { display: 'flex', justifyContent: 'space-between' },
    lineText: { color: COLORS.LT_MIDNIGHT, fontWeight: '400' },
    differenceAmount: { fontWeight: '700', color: COLORS.LT_PEPPER150 },
    divider: { borderColor: COLORS.LT_PEPPER25 },
};

const formatAmount = (n) => {
    const value = Number(n || 0).toFixed(2);
    return `$${value}`;
};

const CorrectionsAmountNoteBox = ({
    mode,
    isSupplemental = false,
    originalAmount = 0,
    newAmount = 0,
    secondaryOriginalAmount = 0,
    secondaryNewAmount = 0,
}) => {
    const firstDifference = Number((originalAmount - newAmount).toFixed(2));
    const secondDifference = Number((secondaryOriginalAmount - secondaryNewAmount).toFixed(2));
    const texts = {
        [ExpenseModeEnum.CreditCard]: {
            originalAmountText: 'Original Charge Amount',
            newAmountText: 'New Allocated Amount',
        },
        [ExpenseModeEnum.ClinicianReimbursement]: {
            originalAmountText: 'Original Reimbursed Amount',
            newAmountText: 'New Reimbursed Amount',
        },
        [ExpenseModeEnum.Payment]: {
            originalAmountText: isSupplemental ? 'Original Bill Amount' : 'Original Amount',
            newAmountText: isSupplemental ? 'New Bill Amount' : 'New Amount',
            secondaryOriginalAmountText: 'Original Pay Amount',
            secondaryNewAmountText: 'New Pay Amount',
        },
    };

    return (
        <Box sx={styles.container} role="alert" aria-live="assertive">
            <Box sx={styles.iconContainer}>
                <InfoIcon sx={styles.icon} />
                <Typography variant="body2" sx={styles.iconText}>
                    Please note.
                </Typography>
            </Box>

            <Box sx={styles.lineContainer}>
                <Typography variant="body2" sx={styles.lineText}>
                    {texts[mode]?.originalAmountText}:
                </Typography>
                <Typography variant="body2" sx={styles.lineText}>
                    {formatAmount(originalAmount)}
                </Typography>
            </Box>

            {isSupplemental && (
                <Box sx={styles.lineContainer}>
                    <Typography variant="body2" sx={styles.lineText}>
                        {texts[mode]?.secondaryOriginalAmountText}:
                    </Typography>
                    <Typography variant="body2" sx={styles.lineText}>
                        {formatAmount(secondaryOriginalAmount)}
                    </Typography>
                </Box>
            )}

            <Box sx={styles.lineContainer}>
                <Typography variant="body2" sx={styles.lineText}>
                    {texts[mode]?.newAmountText}:
                </Typography>
                <Typography variant="body2" sx={styles.lineText}>
                    {formatAmount(newAmount)}
                </Typography>
            </Box>

            {isSupplemental && (
                <Box sx={styles.lineContainer}>
                    <Typography variant="body2" sx={styles.lineText}>
                        {texts[mode]?.secondaryNewAmountText}:
                    </Typography>
                    <Typography variant="body2" sx={styles.lineText}>
                        {formatAmount(secondaryNewAmount)}
                    </Typography>
                </Box>
            )}

            <Divider sx={styles.divider} />

            <Box sx={styles.lineContainer}>
                <Typography variant="body2" sx={styles.lineText}>
                    {isSupplemental ? 'Bill Amount Difference:' : 'Difference:'}
                </Typography>
                <Typography variant="body2" sx={styles.differenceAmount}>
                    {formatAmount(Math.abs(firstDifference))}
                </Typography>
            </Box>
            {isSupplemental && (
                <Box sx={styles.lineContainer}>
                    <Typography variant="body2" sx={styles.lineText}>
                        Pay Amount Difference:
                    </Typography>
                    <Typography variant="body2" sx={styles.differenceAmount}>
                        {formatAmount(Math.abs(secondDifference))}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default CorrectionsAmountNoteBox;
